import { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { InfoCircleFill } from "react-bootstrap-icons";
import { LocalDate } from "@js-joda/core";
import { showDate } from "./Data/Date";

interface CancelPlanModalProps {
  show: boolean;
  onHide: () => void;
  onConfirmCancel: (cancellationDate: string) => void;
  cancellationWindowDays: number;
  defaultCancellationDate?: string;
  nextRenewalDate?: string;
  currentSpaces: number;
  hasWaitlistEntries: boolean;
  isWaitlistEnabled: boolean;
}

export default function UserPlanCancellationModal({
  show,
  onHide,
  onConfirmCancel,
  cancellationWindowDays,
  defaultCancellationDate,
  nextRenewalDate,
  currentSpaces,
  hasWaitlistEntries,
  isWaitlistEnabled,
}: CancelPlanModalProps) {
  const minDate = LocalDate.now().plusDays(cancellationWindowDays).toString();
  const getDefaultDate = () => {
    if (!defaultCancellationDate) return minDate;
    return defaultCancellationDate >= minDate ? defaultCancellationDate : minDate;
  };

  const [cancellationDate, setCancellationDate] = useState(getDefaultDate);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    setShowWarning(hasWaitlistEntries && isWaitlistEnabled);
  }, [hasWaitlistEntries, isWaitlistEnabled]);

  useEffect(() => {
    if (show) {
      setCancellationDate(getDefaultDate());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  function handleHide() {
    onHide();
  }

  function handleConfirm() {
    onConfirmCancel(cancellationDate);
  }

  const minDateDisplay = showDate(LocalDate.parse(minDate));
  const selectedDate = cancellationDate ? LocalDate.parse(cancellationDate) : null;
  const renewalDate = nextRenewalDate ? LocalDate.parse(nextRenewalDate) : null;
  const isAfterNextRenewal =
    selectedDate != null && renewalDate != null && selectedDate.isAfter(renewalDate);

  return (
    <Modal centered show={show} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>Cancel Plan</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">Are you sure you want to cancel your plan?</p>

        <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3" role="alert">
          <InfoCircleFill className="flex-shrink-0 mt-1" />
          <span>
              Your access will continue through the end of the subscription period.
            </span>
        </div>

        {cancellationWindowDays > 0 && (
          <div className="alert alert-info d-flex align-items-start gap-2 mb-3" role="alert">
            <InfoCircleFill className="flex-shrink-0 mt-1" />
            <span>
              This plan requires <strong>{cancellationWindowDays} day{cancellationWindowDays !== 1 ? "s" : ""}</strong>{" "}
              advance notice. The earliest cancellation date is{" "}
              <strong>{minDateDisplay}</strong>. 
            </span>
          </div>
        )}

        {showWarning && (
          <div className="alert alert-warning text-sm mb-3" role="alert">
            You&apos;re releasing {currentSpaces} space{currentSpaces > 1 ? "s" : ""}.
            Parkers on the waitlist will be moved forward.
            If you change your mind, you&apos;ll re-join the back of the waitlist.
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            {cancellationWindowDays > 0 ? "Cancellation date" : "Choose the date your access should end"}
          </Form.Label>
          <Form.Control
            type="date"
            min={minDate}
            value={cancellationDate}
            onChange={(e) => setCancellationDate(e.target.value)}
          />
          {isAfterNextRenewal && (
            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mt-3 mb-0" role="alert">
              <InfoCircleFill className="flex-shrink-0 mt-1" />
              <span>
                Your cancellation date is after your next renewal. You will be charged for the
                following billing period, and your access will continue until the end of that
                period.
              </span>
            </div>
          )}
        </Form.Group>

        <div className="d-flex flex-column gap-3 mt-3">
          <Button variant="danger" onClick={handleConfirm} disabled={!cancellationDate || cancellationDate < minDate}>
            Confirm Cancellation
          </Button>
          <Button variant="outline-secondary" onClick={handleHide}>
            Go Back
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
